import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { TradingPost, TradingSearchParams } from '../types/TradingTypes';
import { searchTradingPosts } from '../utils/tradingUtils';
import '../assets/styles/TradingBlog.css';
import TradingPostCard from '../components/trading/TradingPostCard';

const TradingBlogPage: React.FC = () => {
  const [posts, setPosts] = useState<TradingPost[]>([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);
      
      // Parse search parameters
      const searchParams = new URLSearchParams(location.search);
      const params: TradingSearchParams = {
        query: searchParams.get('query') || undefined,
        tag: searchParams.get('tag') || undefined,
        symbol: searchParams.get('symbol') || undefined
      };
      
      // Search posts based on the parameters
      const searchResults = await searchTradingPosts(params);
      setPosts(searchResults);
      setLoading(false);
    };
    
    loadPosts();
  }, [location.search]);

  // SEO metadata
  const pageTitle = posts.length > 0 
    ? "Trading Blog | Price on Rails | Trading Insights"
    : "Trading Blog | Price on Rails | No Results Found";
  
  const pageDescription = "Explore our daily trading activities, including market analysis, trade entries, exits, and performance metrics. Learn from real trading examples.";

  return (
    <div className="trading-blog-container">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      <h1 className="trading-blog-title">Trading Blog</h1>
      
      {loading ? (
        <div className="trading-blog-loading">Loading trading posts...</div>
      ) : posts.length > 0 ? (
        <>
          <div className="trading-blog-list">
            {posts.map(post => (
              <TradingPostCard key={post.id} post={post} />
            ))}
          </div>
          {/* Sum of all trades from all posts */}
          <div className="trading-blog-total-profit" style={{ textAlign: 'center', marginTop: '2rem', fontWeight: 'bold', fontSize: '1.2rem' }}>
            Total Profit of All Trades: $
            {posts
              .reduce((sum, post) => sum + post.trades.reduce((tSum, trade) => tSum + trade.profit, 0), 0)
              .toFixed(2)}
          </div>
        </>
      ) : (
        <div className="trading-blog-no-results">
          <h3>No trading posts found</h3>
          <p>Try adjusting your search criteria or browse all posts.</p>
        </div>
      )}
    </div>
  );
};

export default TradingBlogPage; 